import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import { graphql } from 'gatsby';
import React, { lazy, LazyExoticComponent, useEffect } from 'react';
import { IContentWithImage } from 'utils/types';

import { SEO } from '../components/SEO';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
const WhyEverelyCareSection = lazy(
  () => import('components/EverlyCarePage/WhyEverelyCare'),
);
const SolutionSliderSection = lazy(() => import('components/SolutionSlider'));
const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const ProductCardGridSection = lazy(() => import('components/ProductCardGrid'));
const StepCardListSection = lazy(() => import('components/StepCardList'));

const VideoSection = lazy(
  () => import('containers/EverlyCarePage/SectionContainer/VideoSection'),
);

type Props = {
  data: {
    everlyCareContent: {
      title: string;
      heroSection: {
        headline: string;
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        componentName: string;
        headline: string;
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container?: any;
      }[];
    };
  };
};

const components: {
  HeroSection: React.FC<any>;
  WhyEverelyCare: LazyExoticComponent<React.FC<any>>;
  SolutionSlider: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
  ProductCardGrid: LazyExoticComponent<React.FC<any>>;
  StepCardList: LazyExoticComponent<React.FC<any>>;
  VideoSection: LazyExoticComponent<React.FC<any>>;
} = {
  HeroSection,
  WhyEverelyCare: WhyEverelyCareSection,
  SolutionSlider: SolutionSliderSection,
  HowItWorksSection: HowItWorksSection,
  ProductCardGrid: ProductCardGridSection,
  StepCardList: StepCardListSection,
  VideoSection,
};

const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStepCardList = (name: string): boolean => name === 'StepCardList';

export const Head = () => <SEO />;

const EverlyCarePage: React.FC<Props> = ({ data }) => {
  // @ts-ignore
  const { hero, sections } = data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS EverlyCare page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['everlyCareContent']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (isProductCardGrid(section.componentName)) {
            return (
              <ProductCardGrid
                key={`${section.componentName}-${i}`}
                {...section}
                title={section.title ?? ''}
                cards={section.container}
                sectionHeader={{
                  ...section,
                  eyebrow: section.title || section.headline,
                  title: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody,
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            );
          }

          if (isStepCardList(section.componentName)) {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            );
          }

          return (
            // @ts-ignore
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default EverlyCarePage;

export const data = graphql`
  query EverlyCarePageQuery {
    contentfulPage001(displayName: { eq: "EHS EverlyCare" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          headline
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        componentName
        backgroundColor
        title
        headline
        description {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
        disclaimer {
          childMarkdownRemark {
            html
          }
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
              }
            }
            image {
              url
            }
          }
        }
        container {
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
            description
            link
          }
        }
      }
    }
  }
`;
